import { Schema, SchemaSettings } from '@orbit/data'
import { findRemoteIdFromNewRecord, KC } from '@/http/utils'
import moment from 'moment-timezone'
import { DEFAULT_CARDIO_EXERCISES, DEFAULT_STRENGTHENING_EXERCISES } from '@/store/modules/exercise/constants'

enum ModelTypes {
  PatientExercise = 'patient-exercise',
}

enum ModelTypeNames {
  PatientExercise = 'patientExercise',
}

export const enum PatientExerciseTypes {
  Cardio = 'cardio',
  Strength = 'strength',
}

export interface CardioExercise {
  type: 'cardio'
  order: number | null
  exerciseName: string | null
  sets: number | null
  equipment: string | null
  resistance: string | null
  bloodOxygenPerc: number | null
  heartRateBpm: number | null
  workload: string | null
  timeMin: number | null
  borgScale: string | null
  o2Ipm: number | null
  feedbackRating: string | null
  feedbackMoreWorkLoad: boolean | null
  comments: string | null
}

export interface StrengtheningExercise {
  type: 'strength'
  order: number | null
  exerciseName: string | null
  sets: number | null
  equipment: string | null
  resistance: string | null
  reps: string | null
  feedbackRating: string | null
  feedbackMoreWorkLoad: boolean | null
  comments: string | null
}

export interface VitalSignReading {
  timestamp: number
  heartRateBpm: number
  bloodOxygenPerc: number
  borgScale: number
  o2Lpm: number
}

export const emptyVitalSignReading = {
  timestamp: null,
  heartRateBpm: null,
  bloodOxygenPerc: null,
  borgScale: null,
  o2Lpm: null,
}

export interface PatientExerciseAttributes {
  patientId: string
  patientFirstName: string
  patientLastName: string
  exerciseDate: string
  createdTimestamp: number
  pmhx: string
  precautions: string
  vitalSignReadings: VitalSignReading[]
  /*
  beginningBloodOxygenPerc: number
  beginningHeartRateBpm: number
  beginningO2Lpm: number
  beginningBorgScale: number
  middleBloodOxygenPerc: number
  middleHeartRateBpm: number
  middleO2Lpm: number
  middleBorgScale: number
  endBloodOxygenPerc: number
  endHeartRateBpm: number
  endO2Lpm: number
  endBorgScale: number
  */
  cardioExercises: CardioExercise[]
  strengtheningExercises: StrengtheningExercise[]
}

export interface PatientExerciseSchema {
  id: string
  attributes: PatientExerciseAttributes
}

const schemaDefinition: SchemaSettings = {
  models: {
    [ModelTypeNames.PatientExercise]: {
      keys: {
        remoteId: {},
      },
      attributes: {
        patientId: { type: 'string' },
        patientFirstName: { type: 'string' },
        patientLastName: { type: 'string' },
        exerciseDate: { type: 'string' },
        createdTimestamp: { type: 'number' },
        pmhx: { type: 'string' },
        precautions: { type: 'string' },
        vitalSignReadings: { type: 'array' },
        /*
        beginningBloodOxygenPerc: { type: 'number' },
        beginningHeartRateBpm: { type: 'number' },
        beginningO2Lpm: { type: 'number' },
        beginningBorgScale: { type: 'number' },
        middleBloodOxygenPerc: { type: 'number' },
        middleHeartRateBpm: { type: 'number' },
        middleO2Lpm: { type: 'number' },
        middleBorgScale: { type: 'number' },
        endBloodOxygenPerc: { type: 'number' },
        endHeartRateBpm: { type: 'number' },
        endO2Lpm: { type: 'number' },
        endBorgScale: { type: 'number' },
        */
        cardioExercises: { type: 'array' },
        strengtheningExercises: { type: 'array' },
      },
    },
  },
}

export async function getExercisesQuery() {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  return remote
    .query(q => {
      console.log('Before calling find record for patient exercises')
      return q.findRecords(ModelTypes.PatientExercise)
    })
    .catch(e => {
      console.log('fetch failed for patient exercises')
      console.log(e)
    })
}

export async function getExercisesQueryByPatient(patientId) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  return remote
    .query(q => {
      console.log('Before calling find record for patient exercise', patientId)
      return q.findRecords(ModelTypes.PatientExercise).filter({ attribute: 'pid', value: patientId })
    })
    .catch(e => {
      console.log('fetch failed for patient exercise')
      console.log(e)
    })
}

interface postExerciseQueryPayload {
  patientId: string
  exerciseDate?: string
  strengtheningExercises?: StrengtheningExercise[]
  cardioExercises?: CardioExercise[]
}

export async function postExerciseQuery(payload: postExerciseQueryPayload) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResourceForNewRecord(schema)
  const result = await remote.push(q => {
    console.log('Before calling push record for patient exercise')
    return q.addRecord({
      id: '',
      type: ModelTypeNames.PatientExercise,
      attributes: {
        patientId: payload.patientId,
        exerciseDate: payload.exerciseDate ? payload.exerciseDate : moment().format('YYYY-MM-DD'),
        strengtheningExercises: payload.strengtheningExercises
          ? payload.strengtheningExercises
          : DEFAULT_STRENGTHENING_EXERCISES,
        cardioExercises: payload.cardioExercises ? payload.cardioExercises : DEFAULT_CARDIO_EXERCISES,
      },
    })
  })
  return findRemoteIdFromNewRecord(result)
}

export async function patchExerciseQuery(payload) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  return remote.push(q => {
    console.log('Before calling update record for patient-exercise')
    return q.updateRecord({
      id: payload.id,
      type: ModelTypeNames.PatientExercise,
      attributes: payload.attributes,
    })
  })
}

export async function deleteExerciseQuery(exerciseId: string) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  return remote.push(q => {
    console.log('Before calling remove record for patient-exercise')
    return q.removeRecord({
      id: exerciseId,
      type: ModelTypeNames.PatientExercise,
    })
  })
}
